/*
 * BONQUERÉ PDP Size Guide
 *
 * Reflects Shopify's native Size option inside the existing Size Guide dialog. The native
 * variant picker remains the only variant-state owner: drawer pills activate its existing control,
 * and every drawer state is re-read from that control after picker morphs.
 */

class BnqSizeGuide extends HTMLElement {
  #abortController;
  #pickerObserver;
  #syncFrame;
  #openFrame;
  #lastTrigger;
  #scrollY = 0;
  #bodyStyles;
  #isClosing = false;
  #reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  #unitKey = 'bnq-size-unit';

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const dialog = this.dialog;
    if (!dialog) return;

    document.addEventListener('click', this.#handleDocumentClick, { signal });
    document.addEventListener('change', this.#handleNativeSelection, { capture: true, signal });
    document.addEventListener('variant:update', this.#handleVariantUpdate, { signal });
    this.addEventListener('click', this.#handleControlClick, { signal });
    dialog.addEventListener('cancel', this.#handleCancel, { signal });
    dialog.addEventListener('close', this.#handleClosed, { signal });

    this.#ensureFallbackTrigger();
    this.#observePicker();
    this.#applyUnit(this.#storedUnit(), false);
    this.#scheduleSync();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#pickerObserver?.disconnect();
    if (this.#syncFrame) cancelAnimationFrame(this.#syncFrame);
    if (this.#openFrame) cancelAnimationFrame(this.#openFrame);
    this.#releaseScrollLock(false);
  }

  get dialog() {
    const dialog = this.querySelector('dialog[data-size-guide-enhanced]');
    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  get panel() {
    return this.dialog?.querySelector('.bnq-sg-dialog__panel') || null;
  }

  get picker() {
    const productId = this.dataset.productId || '';
    return (
      Array.from(
        document.querySelectorAll(
          ".bnq-product-details variant-picker[data-template-product-match='true'][data-product-id]"
        )
      ).find((picker) => picker.getAttribute('data-product-id') === productId) || null
    );
  }

  #handleDocumentClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const opener = event.target.closest('[data-size-guide-open]');
    if (opener && opener.closest('.bnq-product-details')) {
      event.preventDefault();
      this.#open(opener);
      return;
    }

    if (event.target.closest('bnq-size-guide') !== this) return;

    if (event.target.closest('[data-size-guide-close]') || event.target === this.dialog) {
      this.#close();
    }
  };

  #handleControlClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const unitButton = event.target.closest('[data-size-guide-unit]');
    if (unitButton instanceof HTMLButtonElement) {
      this.#applyUnit(unitButton.dataset.sizeGuideUnit === 'in' ? 'in' : 'cm');
      return;
    }

    const sizeButton = event.target.closest('[data-size-guide-size]');
    if (sizeButton instanceof HTMLButtonElement && !sizeButton.disabled) {
      this.#activateNativeSize(sizeButton);
    }
  };

  #handleCancel = (event) => {
    event.preventDefault();
    this.#close();
  };

  #handleClosed = () => {
    const dialog = this.dialog;
    if (dialog) dialog.dataset.state = 'closed';
    this.#isClosing = false;
    this.#releaseScrollLock(true);
  };

  #handleNativeSelection = (event) => {
    const picker = this.picker;
    if (picker && event.target instanceof Node && picker.contains(event.target)) this.#scheduleSync();
  };

  #handleVariantUpdate = (event) => {
    const picker = this.picker;
    if (picker && event.target instanceof Node && picker.contains(event.target)) {
      this.#observePicker();
      this.#scheduleSync();
    }
  };

  #open(trigger) {
    const dialog = this.dialog;
    const panel = this.panel;
    if (!dialog || !panel || dialog.open) return;

    this.#lastTrigger = trigger instanceof HTMLElement ? trigger : null;
    this.#isClosing = false;
    dialog.dataset.state = 'closed';
    this.#syncFromNativePicker();
    this.#lockScroll();
    dialog.showModal();

    // Two frames are intentional: the first paints the translated closed state, and the second
    // advances to open. A single rAF can run before the first post-showModal paint and skip the
    // drawer's real starting frame, which reads as an abrupt jump.
    panel.getBoundingClientRect();
    this.#openFrame = requestAnimationFrame(() => {
      this.#openFrame = requestAnimationFrame(() => {
        this.#openFrame = 0;
        if (!dialog.open) return;
        dialog.dataset.state = 'open';
        const closeButton = dialog.querySelector('[data-size-guide-close]');
        if (closeButton instanceof HTMLElement) closeButton.focus({ preventScroll: true });
      });
    });
  }

  #close() {
    const dialog = this.dialog;
    const panel = this.panel;
    if (!dialog?.open || !panel || this.#isClosing) return;

    const wasWaitingForOpenPaint = Boolean(this.#openFrame);
    if (this.#openFrame) {
      cancelAnimationFrame(this.#openFrame);
      this.#openFrame = 0;
    }

    if (wasWaitingForOpenPaint || this.#reduceMotion?.matches) {
      dialog.close();
      return;
    }

    this.#isClosing = true;
    dialog.dataset.state = 'closing';

    const finish = (event) => {
      if (event.target !== panel || (event.type === 'transitionend' && event.propertyName !== 'transform')) return;
      panel.removeEventListener('transitionend', finish);
      panel.removeEventListener('transitioncancel', finish);
      if (dialog.open) dialog.close();
    };

    panel.addEventListener('transitionend', finish, { signal: this.#abortController.signal });
    panel.addEventListener('transitioncancel', finish, { signal: this.#abortController.signal });
  }

  #lockScroll() {
    const body = document.body;
    if (this.#bodyStyles) return;

    this.#scrollY = window.scrollY;
    this.#bodyStyles = {
      width: body.style.width,
      position: body.style.position,
      top: body.style.top,
      paddingInlineEnd: body.style.paddingInlineEnd,
    };

    const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    const currentPadding = Number.parseFloat(getComputedStyle(body).paddingInlineEnd) || 0;

    body.style.width = '100%';
    body.style.position = 'fixed';
    body.style.top = `-${this.#scrollY}px`;
    if (scrollbarWidth) body.style.paddingInlineEnd = `${currentPadding + scrollbarWidth}px`;
    document.documentElement.setAttribute('data-bnq-size-guide-open', '');
  }

  #releaseScrollLock(restoreFocus) {
    if (!this.#bodyStyles) return;

    const body = document.body;
    const styles = this.#bodyStyles;
    this.#bodyStyles = null;
    body.style.width = styles.width;
    body.style.position = styles.position;
    body.style.top = styles.top;
    body.style.paddingInlineEnd = styles.paddingInlineEnd;
    document.documentElement.removeAttribute('data-bnq-size-guide-open');
    window.scrollTo({ top: this.#scrollY, behavior: 'instant' });

    if (!restoreFocus) return;

    const currentTrigger = document.querySelector('.bnq-product-details [data-size-guide-open]');
    const focusTarget = this.#lastTrigger?.isConnected ? this.#lastTrigger : currentTrigger;
    if (focusTarget instanceof HTMLElement) focusTarget.focus({ preventScroll: true });
    this.#lastTrigger = null;
  }

  #ensureFallbackTrigger() {
    if (document.querySelector('.bnq-product-details [data-size-guide-open]')) return;

    const fallbackData = this.querySelector('[data-size-guide-fallback-trigger]');
    const anchor =
      document.querySelector('.bnq-product-details variant-picker') ||
      document.querySelector('.bnq-product-details .buy-buttons-block');
    if (!(fallbackData instanceof HTMLScriptElement) || !(anchor instanceof Element)) return;

    let label = 'Size guide';
    try {
      label = JSON.parse(fallbackData.textContent || '').label || label;
    } catch (error) {
      // Keep the readable fallback when malformed JSON is supplied.
    }

    const wrap = document.createElement('p');
    wrap.className = 'bnq-sg-fallback-trigger';
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'size-guide-trigger';
    button.setAttribute('data-size-guide-open', '');
    button.textContent = label;
    wrap.appendChild(button);
    anchor.insertAdjacentElement('afterend', wrap);
  }

  #observePicker() {
    this.#pickerObserver?.disconnect();
    const picker = this.picker;
    if (!picker) return;

    this.#pickerObserver = new MutationObserver(() => this.#scheduleSync());
    this.#pickerObserver.observe(picker, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-disabled', 'checked', 'data-current-checked', 'data-option-available', 'selected'],
    });
  }

  #nativeControlFor(button) {
    const picker = this.picker;
    if (!picker) return null;

    const optionValueId = button.dataset.optionValueId || '';
    const controls = Array.from(picker.querySelectorAll('input[data-option-value-id], option[data-option-value-id]'));

    // Option-value IDs are Shopify's stable identity. Never fall back to a value-only search across
    // every option: a Color could legitimately share text with a Size, and must never be changed.
    return controls.find((control) => control.getAttribute('data-option-value-id') === optionValueId) || null;
  }

  #activateNativeSize(button) {
    const control = this.#nativeControlFor(button);
    if (control instanceof HTMLInputElement) {
      if (control.dataset.optionAvailable === 'false' || control.getAttribute('aria-disabled') === 'true') return;
      control.click();
      this.#scheduleSync();
      return;
    }

    if (control instanceof HTMLOptionElement && !control.disabled) {
      const select = control.closest('select');
      if (!(select instanceof HTMLSelectElement)) return;
      select.value = control.value;
      select.dispatchEvent(new Event('input', { bubbles: true }));
      select.dispatchEvent(new Event('change', { bubbles: true }));
      this.#scheduleSync();
    }
  }

  #scheduleSync() {
    if (this.#syncFrame) cancelAnimationFrame(this.#syncFrame);
    this.#syncFrame = requestAnimationFrame(() => {
      this.#syncFrame = 0;
      this.#syncFromNativePicker();
    });
  }

  #syncFromNativePicker() {
    const buttons = Array.from(this.querySelectorAll('[data-size-guide-size]'));
    let selectedSize = '';

    buttons.forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      const control = this.#nativeControlFor(button);
      let selected = false;
      let available = !button.disabled;

      if (control instanceof HTMLInputElement) {
        selected = control.checked;
        available = control.dataset.optionAvailable !== 'false' && control.getAttribute('aria-disabled') !== 'true';
      } else if (control instanceof HTMLOptionElement) {
        selected = control.selected;
        available = !control.disabled;
      }

      button.disabled = !available;
      button.setAttribute('aria-disabled', available ? 'false' : 'true');
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      if (selected) selectedSize = this.#normalizeSize(button.dataset.sizeValue);
    });

    this.querySelectorAll('[data-size-guide-row]').forEach((row) => {
      const matches = Boolean(selectedSize) && this.#normalizeSize(row.getAttribute('data-size-value')) === selectedSize;
      row.toggleAttribute('data-selected', matches);
      if (matches) row.setAttribute('aria-current', 'true');
      else row.removeAttribute('aria-current');
    });
  }

  #normalizeSize(value) {
    return String(value || '').trim().replace(/\s+/g, ' ').toLocaleUpperCase();
  }

  #storedUnit() {
    try {
      return localStorage.getItem(this.#unitKey) === 'in' ? 'in' : 'cm';
    } catch (error) {
      return 'cm';
    }
  }

  #applyUnit(unit, persist = true) {
    const normalizedUnit = unit === 'in' ? 'in' : 'cm';

    this.querySelectorAll('[data-cm-value]').forEach((cell) => {
      const centimeters = cell.getAttribute('data-cm-value') || '';
      cell.textContent = normalizedUnit === 'in' ? this.#toInches(centimeters) : centimeters;
    });

    this.querySelectorAll('[data-size-guide-unit-header]').forEach((header) => {
      const centimeters = header.getAttribute('data-cm-label') || header.textContent || '';
      header.textContent =
        normalizedUnit === 'in'
          ? centimeters.replace(/\(\s*cm\s*\)/i, '(IN)').replace(/\bcm\b/i, 'IN')
          : centimeters;
    });

    this.querySelectorAll('[data-size-guide-unit]').forEach((button) => {
      const active = button.getAttribute('data-size-guide-unit') === normalizedUnit;
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    if (!persist) return;
    try {
      localStorage.setItem(this.#unitKey, normalizedUnit);
    } catch (error) {
      // Storage is optional; the current drawer session remains fully functional.
    }
  }

  #toInches(rawValue) {
    const value = rawValue.trim().replace(/\u00a0/g, ' ');
    const number = '(-?\\d+(?:[.,]\\d+)?)';
    const range = new RegExp(`^${number}\\s*[–—-]\\s*${number}$`);
    const single = new RegExp(`^${number}$`);
    const rangeMatch = value.match(range);

    if (rangeMatch) {
      return `${this.#formatInches(rangeMatch[1])}–${this.#formatInches(rangeMatch[2])}`;
    }

    const singleMatch = value.match(single);
    return singleMatch ? this.#formatInches(singleMatch[1]) : rawValue;
  }

  #formatInches(value) {
    const centimeters = Number.parseFloat(String(value).replace(',', '.'));
    if (!Number.isFinite(centimeters)) return value;

    return new Intl.NumberFormat(document.documentElement.lang || undefined, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(centimeters / 2.54);
  }
}

if (!customElements.get('bnq-size-guide')) customElements.define('bnq-size-guide', BnqSizeGuide);
